import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

export interface AIContextItem {
  id: string;
  type: string;
  label: string;
  data?: unknown;
}

interface AIContextValue {
  items: AIContextItem[];
  setContext: (item: AIContextItem) => void;
  removeContext: (id: string) => void;
  clearAll: () => void;
}

const AIContextCtx = createContext<AIContextValue | null>(null);

export function AIContextProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<AIContextItem[]>(() => {
    try {
      const saved = localStorage.getItem("ai_context");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const persist = (next: AIContextItem[]) => {
    localStorage.setItem("ai_context", JSON.stringify(next));
  };

  const setContext = useCallback((item: AIContextItem) => {
    setItems((prev) => {
      const filtered = prev.filter((i) => i.id !== item.id);
      const next = [...filtered, item];
      persist(next);
      return next;
    });
  }, []);

  const removeContext = useCallback((id: string) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      persist(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    persist([]);
  }, []);

  return (
    <AIContextCtx.Provider
      value={{ items, setContext, removeContext, clearAll }}
    >
      {children}
    </AIContextCtx.Provider>
  );
}

export function useAIContext() {
  const ctx = useContext(AIContextCtx);
  if (!ctx)
    throw new Error("useAIContext must be used within AIContextProvider");
  return ctx;
}
